import type { AudioFileInfo, InputFile } from "@/client/core/Core.api";
import type {
    LabsAudioAssetMetadata,
    LabsAudioResourceMetadata,
    LabsBaseAssetMetadata,
    LabsInputAudioFile,
    LabsInputFile,
    LabsInputJsonFile,
    LabsInputMidiFile,
    LabsJsonResourceMetadata,
    LabsMidiResourceMetadata,
    LabsResourceMetadata,
} from "@/client/core/platform-types/labs.api";
import { kLabsMetadataVersion, sanitizeFileName } from "@/client/core/platform-types/labs.api";

// ── Website service: full resource metadata ──────────────────────────────────

const createFileMetadata = (file: File, idValue: string) => ({
    version: kLabsMetadataVersion,
    originalFilename: file.name,
    filenameOnDisk: idValue,
    dateAdded: new Date().toISOString(),
});

const createBaseAssetDefaults = (
    metadata: Partial<LabsBaseAssetMetadata> | undefined,
): LabsBaseAssetMetadata => ({
    spliceUuid: metadata?.spliceUuid ?? null,
    bpm: metadata?.bpm ?? null,
    chordType: metadata?.chordType ?? null,
    key: metadata?.key ?? null,
    artworkUrl: metadata?.artworkUrl ?? null,
    tags: metadata?.tags ?? [],
});

export const createAudioResourceMetadata = (
    file: File,
    idValue: string,
    audioInfo: AudioFileInfo,
    metadata: Partial<LabsAudioAssetMetadata> | undefined,
): LabsAudioResourceMetadata => ({
    ...createFileMetadata(file, idValue),
    ...createBaseAssetDefaults(metadata),
    type: metadata?.type ?? null,
    duration_s: audioInfo.duration_s,
    sampleRate: audioInfo.sampleRate,
    numberOfChannels: audioInfo.numberOfChannels,
});

export const createMidiResourceMetadata = (
    file: File,
    idValue: string,
    metadata: Partial<LabsBaseAssetMetadata> | undefined,
): LabsMidiResourceMetadata => ({
    ...createFileMetadata(file, idValue),
    ...createBaseAssetDefaults(metadata),
});

export const createJsonResourceMetadata = (file: File, idValue: string): LabsJsonResourceMetadata =>
    createFileMetadata(file, idValue);

export const createResourceMetadata = (
    input: InputFile,
    idValue: string,
    audioInfo: AudioFileInfo | undefined,
): LabsResourceMetadata => {
    switch (input.type) {
        case "wav":
            if (audioInfo === undefined) {
                throw new Error("Audio info is required for wav resources");
            }
            return createAudioResourceMetadata(
                input.file,
                idValue,
                audioInfo,
                input.metadata as Partial<LabsAudioAssetMetadata> | undefined,
            );
        case "mid":
            return createMidiResourceMetadata(
                input.file,
                idValue,
                input.metadata as Partial<LabsBaseAssetMetadata> | undefined,
            );
        case "json":
            return createJsonResourceMetadata(input.file, idValue);
    }
};

// ── Plugin service: input file for platform messages ─────────────────────────

export const createInputFile = (input: InputFile, liveID: number): LabsInputFile => {
    const filename = sanitizeFileName(input.file.name);
    const baseAsset = createBaseAssetDefaults(
        input.metadata as Partial<LabsBaseAssetMetadata> | undefined,
    );

    switch (input.type) {
        case "wav": {
            const audioMeta = input.metadata as Partial<LabsAudioAssetMetadata> | undefined;
            const result: LabsInputAudioFile = {
                type: "wav",
                file: input.file,
                liveID,
                metadata: {
                    filename,
                    ...baseAsset,
                    type: audioMeta?.type ?? null,
                },
            };
            return result;
        }
        case "mid": {
            const result: LabsInputMidiFile = {
                type: "mid",
                file: input.file,
                liveID,
                metadata: {
                    filename,
                    ...baseAsset,
                },
            };
            return result;
        }
        case "json": {
            const result: LabsInputJsonFile = {
                type: "json",
                file: input.file,
                liveID,
            };
            return result;
        }
    }
};
